// Branding and text size (PLAN.md 4.12, brief 1A-5). Pure: no database, no clock.

/** The fonts Settings › Branding offers (mockup). A company can also upload its own (1A-7). */
export const FONTS = [
  "Inter",
  "Source Sans 3",
  "Atkinson Hyperlegible",
  "IBM Plex Sans",
  "Lato",
] as const;
export type Font = (typeof FONTS)[number];

export const TEXT_SIZES = ["small", "standard", "large"] as const;
export type TextSize = (typeof TEXT_SIZES)[number];

/** How much each text size scales the whole page (mockup: 0.92, 1, 1.12). */
export const TEXT_SCALE: Record<TextSize, number> = { small: 0.92, standard: 1, large: 1.12 };

/** FlowCare's own look, used until a company picks its colors. */
export const DEFAULT_BRANDING = {
  appName: "FlowCare",
  primaryColor: "#073E5A",
  accentColor: "#4096AB",
  font: "Inter" as Font,
  textSize: "standard" as TextSize,
};

/** The ready-made color pairs on Settings › Branding (mockup). */
export const READY_MADE: readonly { name: string; primary: string; accent: string }[] = [
  { name: "Abbella", primary: "#073E5A", accent: "#4096AB" },
  { name: "Plum", primary: "#5A2D82", accent: "#B0407A" },
  { name: "Forest", primary: "#1F4E3D", accent: "#2E8B57" },
  { name: "Navy", primary: "#1B3A6B", accent: "#3E7CB1" },
  { name: "Charcoal", primary: "#2F3A40", accent: "#C2702E" },
];

export const APP_NAME_MAX = 40;
const HEX = /^#[0-9A-Fa-f]{6}$/;

export const isTextSize = (v: unknown): v is TextSize =>
  typeof v === "string" && (TEXT_SIZES as readonly string[]).includes(v);

/** A person's text size: their own choice, or the company default when they haven't chosen. */
export function textSizeFor(own: TextSize | null, companyDefault: TextSize): TextSize {
  return own ?? companyDefault;
}

/** Reads a person's text size from a request: small, standard, large, or null for the default. */
export function readTextSize(
  input: unknown,
): { ok: true; value: TextSize | null } | { ok: false; error: string } {
  if (input === null || isTextSize(input)) return { ok: true, value: input };
  return { ok: false, error: "text size is small, standard or large" };
}

/** Branding as the shell sees it, with FlowCare's defaults filled in. */
export interface Branding {
  appName: string;
  primaryColor: string;
  accentColor: string;
  font: Font;
  textSize: TextSize;
  /** Where the company's logo is fetched from (any signed-in person of the company), or null. */
  logo: string | null;
  /** The company's uploaded font, used instead of `font` while set. */
  fontFile: { id: string; name: string; url: string } | null;
  /** The top bar's short name (brief 1D-3); null: the company's name is shown. */
  shortName: string | null;
  /** The top bar shows the logo and the short name, or the logo only. */
  topBar: TopBarShows;
}

export const TOP_BAR = ["logo_name", "logo"] as const;
export type TopBarShows = (typeof TOP_BAR)[number];
export const TOP_BAR_LABEL: Record<TopBarShows, string> = {
  logo_name: "Logo and short name",
  logo: "Logo only",
};
export const SHORT_NAME_MAX = 20;

/** A name's initials, at most two ("Thimbleford Home Care" → "TH"). */
export const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter((w) => /^[A-Za-z0-9]/.test(w))
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

/**
 * The name the top bar shows when no short name is set: the company name's first word, whole
 * (brief 1D-3b: never the full name cut off mid-word).
 */
export const firstWordOf = (name: string) => name.trim().split(/\s+/)[0] ?? "";

/**
 * What the top bar shows (mockup v40): the company's logo, or its initials in the brand color
 * when there's none; the short name (or the first word of the company's name) unless "logo
 * only"; and the full name on hover.
 */
export function topBarOf(
  b: Pick<Branding, "logo" | "shortName" | "topBar">,
  companyName: string,
): { logo: string | null; initials: string; name: string | null; full: string } {
  const short = b.shortName ?? firstWordOf(companyName);
  return {
    logo: b.logo,
    initials: initialsOf(companyName) || "FC",
    name: b.topBar === "logo" && b.logo ? null : short,
    full: companyName,
  };
}

/** The addresses the shell loads the logo and the uploaded font from. The file id busts the cache. */
export const logoUrl = (fileId: string) => `/api/branding/logo?v=${fileId}`;
export const fontUrl = (fileId: string) => `/api/branding/font?v=${fileId}`;

export function brandingFrom(
  row: {
    app_name: string;
    primary_color: string | null;
    accent_color: string | null;
    font: string | null;
    text_size: TextSize;
    logo_file_id: string | null;
    font_file_id: string | null;
    short_name?: string | null;
    top_bar?: TopBarShows;
  } | null,
  /** The uploaded font's name, as shown in the font list. */
  fontFileName: string | null = null,
): Branding {
  if (!row)
    return {
      ...DEFAULT_BRANDING,
      logo: null,
      fontFile: null,
      shortName: null,
      topBar: "logo_name",
    };
  return {
    appName: row.app_name || DEFAULT_BRANDING.appName,
    primaryColor: (row.primary_color ?? DEFAULT_BRANDING.primaryColor).toUpperCase(),
    accentColor: (row.accent_color ?? DEFAULT_BRANDING.accentColor).toUpperCase(),
    font: (FONTS as readonly string[]).includes(row.font ?? "") ? (row.font as Font) : "Inter",
    textSize: row.text_size,
    logo: row.logo_file_id ? logoUrl(row.logo_file_id) : null,
    fontFile: row.font_file_id
      ? {
          id: row.font_file_id,
          name: fontFileName ?? "Uploaded font",
          url: fontUrl(row.font_file_id),
        }
      : null,
    shortName: row.short_name ?? null,
    topBar: row.top_bar ?? "logo_name",
  };
}

/** The columns of tenant_branding a change sets. */
export interface BrandingChange {
  app_name?: string;
  primary_color?: string;
  accent_color?: string;
  font?: Font;
  text_size?: TextSize;
  logo_file_id?: string | null;
  font_file_id?: string | null;
  short_name?: string | null;
  top_bar?: TopBarShows;
}

const FILE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * Reads a branding change from a request body (appName, primaryColor, accentColor, font,
 * textSize, logoFileId, fontFileId; any of them; a file id may be null to take it off). Returns the columns to set or why the change is refused.
 */
export function readBranding(
  input: unknown,
): { ok: true; change: BrandingChange } | { ok: false; error: string } {
  if (typeof input !== "object" || input === null || Array.isArray(input))
    return { ok: false, error: "send the branding to change" };
  const b = input as Record<string, unknown>;
  const change: BrandingChange = {};
  if (b.appName !== undefined) {
    if (typeof b.appName !== "string" || !b.appName.trim())
      return { ok: false, error: "the app name can't be empty" };
    if (b.appName.trim().length > APP_NAME_MAX)
      return { ok: false, error: `the app name is at most ${APP_NAME_MAX} characters` };
    change.app_name = b.appName.trim();
  }
  for (const [key, col] of [
    ["primaryColor", "primary_color"],
    ["accentColor", "accent_color"],
  ] as const) {
    const v = b[key];
    if (v === undefined) continue;
    if (typeof v !== "string" || !HEX.test(v))
      return { ok: false, error: "a color is # and six hex digits, like #073E5A" };
    change[col] = v.toUpperCase();
  }
  if (b.font !== undefined) {
    if (!(FONTS as readonly unknown[]).includes(b.font))
      return { ok: false, error: "pick a font from the list" };
    change.font = b.font as Font;
  }
  if (b.textSize !== undefined) {
    if (!isTextSize(b.textSize))
      return { ok: false, error: "text size is small, standard or large" };
    change.text_size = b.textSize;
  }
  for (const [key, col] of [
    ["logoFileId", "logo_file_id"],
    ["fontFileId", "font_file_id"],
  ] as const) {
    const v = b[key];
    if (v === undefined) continue;
    if (v !== null && (typeof v !== "string" || !FILE_ID.test(v)))
      return { ok: false, error: "that file isn't one of this company's uploads" };
    change[col] = v;
  }
  if (b.shortName !== undefined) {
    if (b.shortName === null || b.shortName === "") change.short_name = null;
    else if (typeof b.shortName !== "string" || !b.shortName.trim())
      return { ok: false, error: "the short name is words, or empty for the company's name" };
    else if (b.shortName.trim().length > SHORT_NAME_MAX)
      return { ok: false, error: `the short name is at most ${SHORT_NAME_MAX} characters` };
    else change.short_name = b.shortName.replace(/\s+/g, " ").trim();
  }
  if (b.topBar !== undefined) {
    if (!(TOP_BAR as readonly unknown[]).includes(b.topBar))
      return { ok: false, error: "the top bar shows the logo and short name, or the logo only" };
    change.top_bar = b.topBar as TopBarShows;
  }
  if (Object.keys(change).length === 0) return { ok: false, error: "nothing to change" };
  return { ok: true, change };
}

// ---- Colors ----------------------------------------------------------------------------------

function channels(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** A color made darker (f < 0) or lighter (f > 0), as the mockup derives link text from the accent. */
export function shade(hex: string, f: number): string {
  const m = (c: number) =>
    Math.max(0, Math.min(255, Math.round(f < 0 ? c * (1 + f) : c + (255 - c) * f)));
  return `#${channels(hex)
    .map((c) => m(c).toString(16).padStart(2, "0"))
    .join("")}`.toUpperCase();
}

function luminance(hex: string): number {
  const [r, g, b] = channels(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two colors, 1 to 21. */
export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/** White text on the main color (every primary button) must be readable: 4.5 to 1 or better. */
export function buttonTextReadable(primary: string): boolean {
  return contrast("#FFFFFF", primary) >= 4.5;
}
